import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import apiService from '../../services/api';
import SafeIcon from '../../common/SafeIcon';
import * as echarts from 'echarts';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiActivity, FiCalendar, FiFilter } = FiIcons;

const ElementHistory = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    if (user?.teamId) {
      loadHistoryData();
    }
    
    return () => {
      if (chartInstance.current) {
        chartInstance.current.dispose();
      }
    };
  }, [user]);

  const loadHistoryData = async () => {
    try {
      setLoading(true);
      
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 90); // Last 90 days
      const startDateString = startDate.toISOString().split('T')[0];

      // 1. Fetch Basic Data (Members, Active Elements)
      const [teamMembersRes, teamUsesElementRes] = await Promise.all([
        apiService.getUserTeamRelations({ team_id: user.teamId, limit: 1000 }),
        apiService.getTeamUsesElement({ team_id: user.teamId, is_used: 1, limit: 1000 })
      ]);

      const memberIds = teamMembersRes.data ? teamMembersRes.data.map(m => m.user_id) : [];
      if (user.id && !memberIds.includes(parseInt(user.id))) memberIds.push(parseInt(user.id));

      if (!teamUsesElementRes.data || teamUsesElementRes.data.length === 0) {
        setLoading(false);
        return;
      }

      const activeElementIds = teamUsesElementRes.data.map(r => r.element_id);

      // 2. Fetch Elements and Questions
      const [elementsRes, questionsRes] = await Promise.all([
        apiService.getElements({ 'id[in]': activeElementIds.join(',') }),
        apiService.getQuestions({ 'element_id[in]': activeElementIds.join(','), limit: 1000 })
      ]);

      const elements = elementsRes.data || [];
      const questions = questionsRes.data || [];
      
      // Map Question ID -> Element ID
      const questionToElementMap = {};
      questions.forEach(q => {
        questionToElementMap[q.id] = q.element_id;
      });

      // Map Element ID -> Element Title
      const elementMap = {};
      elements.forEach(el => {
        elementMap[el.id] = el.title;
      });

      // 3. Fetch Evaluations
      if (memberIds.length === 0) {
        setLoading(false);
        return;
      }

      const evaluationsRes = await apiService.getEvaluations({
        'user_id[in]': memberIds.join(','),
        'evaluation_date[gte]': startDateString,
        limit: 5000 
      });

      const evaluations = evaluationsRes.data || [];

      // 4. Process Data
      processChartData(evaluations, questionToElementMap, elementMap, startDate, endDate);

    } catch (error) {
      console.error('Error loading element history:', error);
    } finally {
      setLoading(false);
    }
  };

  const processChartData = (evaluations, qToEMap, eMap, startDate, endDate) => {
    const dates = [];
    let currentDate = new Date(startDate);
    
    // Normalize time to avoid issues
    const endDateTime = new Date(endDate).setHours(0,0,0,0);
    currentDate.setHours(0,0,0,0);
    
    // Generate dates
    while (currentDate <= endDateTime) {
      const year = currentDate.getFullYear();
      const month = String(currentDate.getMonth() + 1).padStart(2, '0');
      const day = String(currentDate.getDate()).padStart(2, '0');
      dates.push(`${year}-${month}-${day}`);
      currentDate.setDate(currentDate.getDate() + 1);
    }

    // Group evaluations by Date -> Element -> Scores
    // Structure: { "2023-01-01": { elementId1: [score, score], elementId2: [score] } }
    const groupedData = {};

    evaluations.forEach(ev => {
        const date = ev.evaluation_date.split('T')[0];
        const elementId = qToEMap[ev.question_id];
        
        if (elementId && eMap[elementId]) {
            if (!groupedData[date]) groupedData[date] = {};
            if (!groupedData[date][elementId]) groupedData[date][elementId] = [];
            groupedData[date][elementId].push(ev.evaluation_value);
        }
    });

    // Prepare Series
    const elementIds = Object.keys(eMap);
    const seriesList = elementIds.map(eId => {
        // Variable to hold the last known value for this specific element (Forward Fill)
        let lastValue = null;

        const data = dates.map(date => {
            const dayData = groupedData[date];
            
            // Check if there is new data for this element on this specific date
            if (dayData && dayData[eId] && dayData[eId].length > 0) {
                const sum = dayData[eId].reduce((a, b) => a + b, 0);
                const avg = parseFloat((sum / dayData[eId].length).toFixed(1));
                lastValue = avg; // Update last known value
                return avg;
            }
            
            // If no new data, return last known value (Forward Fill)
            return lastValue;
        });

        // Only return series if it has at least one non-null data point
        if (data.every(d => d === null)) return null;

        return {
            name: eMap[eId],
            type: 'line',
            data: data,
            smooth: true,
            connectNulls: true, // Connect lines if there are still gaps (e.g. at start)
            showSymbol: false,
            symbolSize: 6
        };
    }).filter(s => s !== null);

    renderChart(dates, seriesList);
  };

  const renderChart = (dates, seriesList) => {
    if (!chartRef.current) return;

    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current);
    }

    const option = {
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#e5e7eb',
        borderWidth: 1,
        textStyle: { color: '#1f2937' },
        order: 'valueDesc'
      },
      legend: {
        type: 'scroll',
        bottom: 0,
        padding: [15, 5],
        textStyle: { color: '#4b5563' }
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '15%', // Space for legend
        top: '3%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: dates,
        axisLine: { lineStyle: { color: '#9ca3af' } },
        axisLabel: {
          color: '#6b7280',
          formatter: (value) => {
            const date = new Date(value);
            return `${date.getDate()}.${date.getMonth() + 1}.`;
          }
        }
      },
      yAxis: {
        type: 'value',
        min: 1,
        max: 7,
        splitNumber: 6,
        axisLabel: { color: '#6b7280' },
        splitLine: { lineStyle: { color: '#f3f4f6' } }
      },
      series: seriesList
    };

    chartInstance.current.setOption(option);

    const handleResize = () => chartInstance.current && chartInstance.current.resize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  };

  return (
    <div className="flex flex-col h-full bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="flex items-center justify-between p-6 border-b border-gray-100">
        <div className="flex items-center">
          <button 
            onClick={() => navigate('/dashboard')}
            className="mr-4 p-2 rounded-full hover:bg-gray-100 text-gray-500 transition-colors"
          >
            <SafeIcon icon={FiArrowLeft} className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-brand-dark flex items-center">
              <SafeIcon icon={FiActivity} className="w-6 h-6 mr-2 text-brand-secondary" />
              Element-Verlauf
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Entwicklung der Element-Scores (Werte werden fortgeschrieben, bis neue Bewertungen erfolgen)
            </p>
          </div>
        </div>
        
        <div className="hidden sm:flex items-center bg-gray-50 px-3 py-1.5 rounded-md text-xs text-gray-500 border border-gray-100">
          <SafeIcon icon={FiCalendar} className="w-3 h-3 mr-2" />
          Letzte 90 Tage
        </div>
      </div>

      <div className="flex-grow p-6 relative min-h-[400px]">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
          </div>
        )}
        
        <div ref={chartRef} className="w-full h-full min-h-[400px]" />
      </div>
    </div>
  );
};

export default ElementHistory;